import api from './apiClient';

// Register a new user
export const register = async (userData) => {
  try {
    // Use email as username
    const registrationData = {
      ...userData,
      username: userData.email // Set username equal to email
    };
    
    const response = await api.post('/auth/register', registrationData);
    return response.data;
  } catch (error) {
    throw error.response?.data || { detail: 'Registration failed' };
  }
};

// Login user and get token
export const login = async (email, password) => {
  try {
    console.log('Login attempt for email:', email);
    
    // The backend expects form data for OAuth2
    const formData = new FormData();
    formData.append('username', email); // OAuth2 expects 'username' field, we use email
    formData.append('password', password);
    
    // Debug the form data
    console.log('Form data entries:');
    for (let pair of formData.entries()) {
      console.log(pair[0] + ': ' + pair[1]);
    }
    
    // Convert FormData to URLSearchParams for proper x-www-form-urlencoded format
    const params = new URLSearchParams();
    params.append('username', email);
    params.append('password', password);
    
    console.log('Sending login request with URLSearchParams');
    const response = await api.post('/auth/login', params, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      }
    });
    console.log('Login response:', response.data);
    return response.data;
  } catch (error) {
    console.error('Login error details:', error.response?.status, error.response?.data);
    
    // Create a more detailed error object
    const errorData = {
      status: error.response?.status,
      detail: error.response?.data?.detail || 'Login failed',
      isLoginError: true
    };
    
    // Customize error message based on status code
    if (error.response?.status === 401) {
      errorData.detail = error.response?.data?.detail || 'Incorrect email or password';
      errorData.isCredentialsError = true;
    } else if (error.response?.status === 404) {
      errorData.detail = 'User not found';
      errorData.isUserNotFound = true;
    }
    
    throw errorData;
  }
};

// Request email verification
export const requestEmailVerification = async (email) => {
  try {
    const response = await api.post('/auth/verify-email/request/', { email });
    return response.data;
  } catch (error) {
    // Check specifically for already verified error
    if (error.response?.status === 400 && 
        (error.response?.data?.detail?.includes('already verified') || 
         error.response?.data?.message?.includes('already verified'))) {
      // Format the error to make it easy to detect
      throw { 
        ...error.response?.data,
        status: 400,
        detail: 'This email is already verified.',
        already_verified: true
      };
    }
    throw error.response?.data || { detail: 'Verification request failed' };
  }
};

// Confirm email with token
export const confirmEmail = async (token) => {
  try {
    const response = await api.post('/auth/verify-email/confirm', { token });
    
    // Update the local user data with verified status
    try {
      const userData = JSON.parse(localStorage.getItem('user'));
      if (userData) {
        userData.is_verified = true;
        userData.isVerified = true;
        localStorage.setItem('user', JSON.stringify(userData));
      }
    } catch (e) {
      console.error('Error updating local user verification status:', e);
    }
    
    return {
      ...response.data,
      is_verified: true
    };
  } catch (error) {
    // Check specifically for already verified error
    if (error.response?.status === 400 && 
        (error.response?.data?.detail?.includes('already verified') || 
         error.response?.data?.message?.includes('already verified'))) {
      // Format the error to make it easy to detect
      throw { 
        ...error.response?.data,
        status: 400,
        detail: 'This email is already verified.',
        already_verified: true
      };
    }
    throw error.response?.data || { detail: 'Email verification failed' };
  }
};

// Check if a user is verified
export const checkVerificationStatus = async (email) => {
  try {
    console.log(`Checking verification status for ${email}`);
    // First try the dedicated endpoint
    try {
      const response = await api.get(`/auth/verify-email/status?email=${encodeURIComponent(email)}`);
      console.log('Verification status response:', response.data);
      return response.data;
    } catch (endpointError) {
      console.log('Specific endpoint not available, trying alternative:', endpointError);
      
      // If dedicated endpoint doesn't exist, try alternative methods
      
      // Try to get user profile if authenticated
      if (localStorage.getItem('token')) {
        try {
          const profileResponse = await api.get('/profiles/me/');
          console.log('Profile response:', profileResponse.data);
          return {
            is_verified: profileResponse.data.is_verified || profileResponse.data.isVerified || false,
            email: profileResponse.data.email || email
          };
        } catch (profileError) {
          console.log('Profile fetch failed:', profileError);
        }
      }
      
      // Try verification request endpoint, which might error with 'already verified'
      try {
        await api.post('/auth/verify-email/request/', { email });
        // If this succeeds, user is not verified
        return { is_verified: false, email };
      } catch (verifyError) {
        console.log('Verification request error:', verifyError);
        // Check if error indicates user is already verified
        if (verifyError.response?.status === 400 && 
            (verifyError.response?.data?.detail?.includes('already verified') ||
             verifyError.response?.data?.message?.includes('already verified'))) {
          return { is_verified: true, email };
        }
      }
      
      // If all alternatives fail, return default
      return { is_verified: false, email };
    }
  } catch (error) {
    console.error('Error checking verification status:', error);
    throw error.response?.data || { detail: 'Failed to check verification status' };
  }
};

// Get current user profile
export const getUserProfile = async () => {
  try {
    const response = await api.get('/profiles/me');
    return response.data;
  } catch (error) {
    throw error.response?.data || { detail: 'Failed to get user profile' };
  }
};

// Update user profile
export const updateUserProfile = async (profileData) => {
  try {
    const response = await api.put('/profiles/me', profileData);
    return response.data;
  } catch (error) {
    throw error.response?.data || { detail: 'Failed to update profile' };
  }
};

// Change password
export const changePassword = async (currentPassword, newPassword) => {
  try {
    const response = await api.post('/profiles/change-password/', {
      current_password: currentPassword,
      new_password: newPassword
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || { detail: 'Password change failed' };
  }
};

// Forgot password
export const forgotPassword = async (email) => {
  try {
    const response = await api.post('/auth/forgot-password/', { email });
    return response.data;
  } catch (error) {
    throw error.response?.data || { detail: 'Password reset request failed' };
  }
};

// Reset password with token
export const resetPassword = async (token, newPassword) => {
  try {
    const response = await api.post('/auth/reset-password/', { 
      token,
      new_password: newPassword
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || { detail: 'Password reset failed' };
  }
};

// Helper to check if user is authenticated
export const isAuthenticated = () => {
  return localStorage.getItem('token') !== null;
};

export default {
  register,
  login,
  requestEmailVerification,
  confirmEmail,
  checkVerificationStatus,
  getUserProfile,
  updateUserProfile,
  changePassword,
  forgotPassword,
  resetPassword,
  isAuthenticated
}; 